import * as React from 'react'
import { SafeAreaView, ScrollView, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Choice, { Option } from '@components/choice/Choice'
import TextInput from '@components/textInput/TextInput'
import Button from '@components/button/Button'
import Icon from '@components/icon/Icons'

import { useFormik } from 'formik'

import useI18n from '@i18n/useI18n'

interface Values {
  reponses: ControleResponseForm[]
}

interface Props extends Values {
  questions: ControleQualiteQuestionResponse[]
  title: string
  localisation: string
  onResponse: (vals: Values, partial?: boolean) => void
  onBack: () => void
}

const Response = ({ localisation, onBack, onResponse, questions, reponses, title }: Props) => {
  const i18n = useI18n()

  const scroll = React.useRef<ScrollView>(null)

  const [step, setStep] = React.useState(Math.min(reponses.length, questions.length - 1))

  const init: Values = React.useMemo(() => ({ reponses }), [reponses])

  const onSubmit = (vals: Values) => {
    onResponse(vals)
  }

  const { values, setFieldValue, handleSubmit } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  const question = questions[step]
  const response = values.reponses.find((r) => r.questionId === question.id)

  React.useEffect(() => {
    if (!response) {
      onField('conformite', 'CONFORME')
    }
  }, [response])

  React.useEffect(() => {
    if (scroll.current) {
      scroll.current.scrollTo({ y: 0, animated: true })
    }
  }, [step])

  const onField = React.useCallback(
    (name: keyof ControleResponseForm, val: string) => {
      const res =
        response || ({ localisationId: localisation, questionId: question.id } as Partial<ControleResponseForm>)
      const next = { ...res, [name]: val }

      const updated = [...values.reponses.filter((r) => r.questionId !== next.questionId), next]

      setFieldValue('reponses', updated)
    },
    [values, question, response]
  )

  const onNext = () => {
    if (step === questions.length - 1) {
      // last question
      handleSubmit()
    } else {
      // submit partial response
      onResponse(values, true)

      //next step
      setStep(step + 1)
    }
  }

  const onPrevious = () => {
    if (step === 0) {
      // first step
      onBack()
    } else {
      setStep(step - 1)
    }
  }

  const options: Option[] = ['CONFORME', 'NON_CONFORME_MINEUR', 'NON_CONFORME_MAJEUR', 'NON_CONCERNE'].map((o) => ({
    label: i18n.t(`screens.controles.form.questions.conformite.${o}`),
    value: o,
  }))

  const option = options.find((o) => !!response && o.value === response.conformite)

  const value = values.reponses.find((v) => v.questionId === question.id)
  const error =
    !value ||
    ((value.conformite === 'NON_CONFORME_MINEUR' || value.conformite === 'NON_CONFORME_MAJEUR') && !value.commentaire)

  return (
    <MainContainer>
      <Count>{i18n.t('screens.controles.form.questions.question', { count: step + 1, max: questions.length })}</Count>

      <ContentContainer ref={scroll} contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 36 }}>
        <Title>{title}</Title>
        <Title>{question.theme}</Title>
        <SubTitle>{question.libelle}</SubTitle>
        <Description>{question.description}</Description>

        <Choice data={options} onItemSelected={(opt) => onField('conformite', opt.value)} value={option} inline />

        <SubTitle>{i18n.t('screens.controles.form.questions.commentaire')}</SubTitle>
        <Description>{i18n.t('screens.controles.form.questions.commentMandatory')}</Description>

        <TextInput
          key={question.id}
          value={response && response.commentaire}
          onChange={(val) => onField('commentaire', val)}
          placeholder={i18n.t('screens.controles.form.questions.comment')}
          numberOfLines={5}
        />
      </ContentContainer>

      <Buttons>
        <ButtonContainer>
          <Button
            label={i18n.t('screens.incident.previous')}
            status="active"
            onClick={onPrevious}
            textOnly
            color="light"
            textColor={theme.colors.primaryText}
          />
        </ButtonContainer>
        <ButtonContainer>
          <Button label={i18n.t('common.validate')} status={error ? 'disabled' : 'active'} onClick={onNext} />
        </ButtonContainer>
      </Buttons>

      <Quit activeOpacity={0.8} onPress={onBack}>
        <Icon name="close" size={20} />
      </Quit>
    </MainContainer>
  )
}

export default Response

const MainContainer = styled(View)`
  margin-top: 20px;
  flex: 1;
`

const Count = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  margin-bottom: 15px;
  text-align: center;
`

const ContentContainer = styled(ScrollView)`
  flex: 1;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 15px;
  margin-right: 30px;
`

const SubTitle = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  margin-bottom: 15px;
`

const Description = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 15px;
`

const Buttons = styled(SafeAreaView)`
  background-color: ${(props) => props.theme.colors.background};
  flex-direction: row;
  justify-content: space-between;
  border: 0 solid ${(props) => props.theme.colors.primaryLight};
  border-top-width: 1px;
`

const ButtonContainer = styled(View)`
  margin: 16px;
`

const Quit = styled(TouchableOpacity)`
  position: absolute;
  top: 0;
  right: 0;
  padding: 0 20px;
`
